import { BrandUrlNameField } from "@/components/brand-url-name-field";
import { useBrandUrlNames, reconcileBrandUrlNameCache } from "@/lib/brand-url-routing";
import { brandPath, canEditBrandUrlName } from "@shared/brand-url-name";
import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Building2, ChevronLeft, ChevronRight, Check, Sparkles,
  Target, Volume2, Shield, Eye, Compass, Swords, Heart,
  Flag, BookOpen, CheckCircle, XCircle, AlertTriangle,
  Plus, X, ArrowLeft, Upload, ImageIcon, Trash2, Database,
  Globe, GripVertical, Pencil, Palette, ChevronDown, Loader2,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BrandDesignSystemEditor } from "@/components/brand-design-system-tab";
import { emptyBrandDesignSystem, type BrandDesignSystem } from "@shared/schema";
import { buildBrandUpdatePayload, BRAND_TAG_FIELDS } from "@shared/brand-update-payload";
import { BrandJsonField, hasBrandValue, isStringList } from "@/components/brand-json-field";
import {
  SiInstagram, SiTiktok, SiX, SiYoutube, SiFacebook,
  SiLinkedin, SiPinterest, SiThreads, SiSnapchat, SiReddit,
} from "react-icons/si";
import type { IconType } from "react-icons";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import type { Workspace, Brand } from "@shared/schema";
import { trackAIResearch } from "@/hooks/use-ai-research-tracker";

type StepId = "basics" | "strategic" | "tone" | "guardrails" | "design" | "advanced" | "review";

const STEPS: { id: StepId; label: string; icon: typeof Building2 }[] = [
  { id: "basics", label: "Basics", icon: Building2 },
  { id: "strategic", label: "Strategic DNA", icon: Target },
  { id: "tone", label: "Tone & Voice", icon: Volume2 },
  { id: "guardrails", label: "Guardrails & Rules", icon: Shield },
  { id: "design", label: "Design System", icon: Palette },
  { id: "advanced", label: "Advanced (Optional)", icon: Database },
  { id: "review", label: "Review & Create", icon: Eye },
];

interface BrandFormData {
  name: string;
  slug: string;
  category: string;
  url: string;
  logoUrl: string;
  adgenLogoUrl: string;
  summary: string;
  positioning: string;
  worldview: string;
  enemy: string;
  promise: string;
  founderStory: string;
  mission: string;
  toneSummary: string;
  approvedPhrases: unknown;
  bannedPhrases: unknown;
  proofHierarchy: unknown;
  claimGuardrails: unknown;
  visualRules: string;
  colorPalette: { label: string; hex: string }[];
  nonNegotiableTruths: unknown;
  // Advanced / AI research fields
  categoryLanguage: unknown;
  pricePositioning: string;
  targetAudienceSignals: unknown;
  keyProducts: unknown;
  socialProfiles: string;
  researchSources: unknown;
  researchNotes: string;
  researchConfidence: number | null;
  productPhilosophy: string;
  officialUrl: string;
  strategicPriorities: unknown;
  brandDesignSystem: BrandDesignSystem;
}

const EMPTY_FORM: BrandFormData = {
  name: "", slug: "", category: "", url: "", logoUrl: "", adgenLogoUrl: "", summary: "",
  positioning: "", worldview: "", enemy: "", promise: "",
  founderStory: "", mission: "",
  toneSummary: "",
  approvedPhrases: [], bannedPhrases: [],
  proofHierarchy: [], claimGuardrails: [],
  visualRules: "", colorPalette: [], nonNegotiableTruths: [],
  categoryLanguage: [], pricePositioning: "", targetAudienceSignals: [],
  keyProducts: [], socialProfiles: "", researchSources: [],
  researchNotes: "", researchConfidence: null, productPhilosophy: "",
  officialUrl: "", strategicPriorities: [],
  brandDesignSystem: emptyBrandDesignSystem(),
};

function editablePalette(value: unknown): BrandFormData["colorPalette"] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(entry => {
    if (typeof entry === "string" && /^#?[\da-f]{3}(?:[\da-f]{3})?$/i.test(entry)) {
      return [{ label: "", hex: entry.startsWith("#") ? entry : `#${entry}` }];
    }
    if (entry && typeof entry === "object" && typeof entry.label === "string" && typeof entry.hex === "string") return [entry];
    return [];
  });
}

function reviewEntries(value: unknown, depth = 0): string[] {
  if (depth > 12 || value == null) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(entry => reviewEntries(entry, depth + 1));
  if (typeof value === "object") return Object.entries(value).flatMap(([key, entry]) =>
    reviewEntries(entry, depth + 1).map(text => `${key}: ${text}`));
  return [String(value)];
}

function TagInput({ values, onChange, placeholder, accentColor = "primary" }: {
  values: unknown;
  onChange: (v: unknown) => void;
  placeholder: string;
  accentColor?: string;
}) {
  if (values == null || isStringList(values)) {
    return <StringTagInput values={values ?? []} onChange={onChange} placeholder={placeholder} accentColor={accentColor} />;
  }
  return <BrandJsonField value={values} onChange={onChange} label={placeholder.replace(/^Add (?:an? )?/, "").replace(/\.+$/, "")} />;
}

function StringTagInput({ values, onChange, placeholder, accentColor = "primary" }: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  accentColor?: string;
}) {
  const [input, setInput] = useState("");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  // Index where the dragged item will be inserted (0..values.length).
  const [insertAt, setInsertAt] = useState<number | null>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the edit textarea to fit content.
  useEffect(() => {
    const el = editTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [editValue, editingIdx]);

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput("");
    }
  };

  const removeTag = (idx: number) => {
    if (editingIdx === idx) cancelEdit();
    onChange(values.filter((_, i) => i !== idx));
  };

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditValue(values[idx]);
    requestAnimationFrame(() => editTextareaRef.current?.focus());
  };

  const commitEdit = () => {
    if (editingIdx == null) return;
    const trimmed = editValue.trim();
    if (!trimmed) {
      onChange(values.filter((_, i) => i !== editingIdx));
    } else if (trimmed !== values[editingIdx] && values.includes(trimmed)) {
      // Duplicate — exit without saving.
    } else {
      onChange(values.map((v, i) => (i === editingIdx ? trimmed : v)));
    }
    setEditingIdx(null);
    setEditValue("");
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditValue("");
  };

  const reorder = (from: number, insertIndex: number) => {
    // insertIndex is the position the item will occupy AFTER removal of `from`.
    if (from < 0 || from >= values.length) return;
    let target = insertIndex;
    if (target < 0) target = 0;
    if (target > values.length) target = values.length;
    // Compute final landing position once `from` is removed.
    const afterRemoval = target > from ? target - 1 : target;
    if (afterRemoval === from) return;
    const next = [...values];
    const [moved] = next.splice(from, 1);
    next.splice(afterRemoval, 0, moved);
    onChange(next);
  };

  const colorMap: Record<string, string> = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    red: "bg-red-500/10 text-red-700 dark:text-red-400",
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    destructive: "bg-destructive/10 text-destructive",
  };
  const indicatorMap: Record<string, string> = {
    primary: "bg-primary",
    emerald: "bg-emerald-500",
    red: "bg-red-500",
    amber: "bg-amber-500",
    destructive: "bg-destructive",
  };
  const tagColor = colorMap[accentColor] || colorMap.primary;
  const indicatorColor = indicatorMap[accentColor] || indicatorMap.primary;

  // Decide whether to show the drop indicator above index `i` (i.e. insertAt === i).
  const showIndicatorBefore = (i: number) =>
    dragIdx !== null && insertAt === i && i !== dragIdx && i !== dragIdx + 1;
  const showIndicatorAfterLast = () =>
    dragIdx !== null && insertAt === values.length && dragIdx !== values.length - 1;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          placeholder={placeholder}
          className="flex-1"
          data-testid={`input-tag-${placeholder.toLowerCase().replace(/\s+/g, "-")}`}
        />
        <Button type="button" variant="outline" size="sm" onClick={addTag} disabled={!input.trim()} data-testid="button-add-tag">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      {values.length > 0 && (
        <div
          className="flex flex-col gap-1"
          onDragOver={(e) => {
            // Allow drop in the empty trailing area below the last item.
            if (dragIdx === null) return;
            e.preventDefault();
          }}
        >
          {values.map((v, i) => {
            const isEditing = editingIdx === i;
            const isBeingDragged = dragIdx === i;
            return (
              <div key={i} className="relative">
                {/* Drop indicator above this row */}
                <div
                  aria-hidden
                  className={`absolute -top-0.5 left-0 right-0 h-0.5 rounded-full transition-opacity ${indicatorColor} ${
                    showIndicatorBefore(i) ? "opacity-100" : "opacity-0"
                  }`}
                />
                <div
                  className={`group flex w-full items-start gap-2 text-xs rounded-2xl pl-2 pr-2 py-1.5 leading-snug transition-all ${tagColor} ${
                    isBeingDragged ? "opacity-40" : ""
                  }`}
                  data-testid={`tag-${i}`}
                  onDragOver={(e) => {
                    if (dragIdx === null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    const rect = e.currentTarget.getBoundingClientRect();
                    const isUpperHalf = e.clientY < rect.top + rect.height / 2;
                    setInsertAt(isUpperHalf ? i : i + 1);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIdx !== null && insertAt !== null) reorder(dragIdx, insertAt);
                    setDragIdx(null);
                    setInsertAt(null);
                  }}
                >
                  <span
                    draggable={!isEditing}
                    onDragStart={(e) => {
                      setDragIdx(i);
                      e.dataTransfer.effectAllowed = "move";
                      try { e.dataTransfer.setData("text/plain", String(i)); } catch {}
                    }}
                    onDragEnd={() => {
                      setDragIdx(null);
                      setInsertAt(null);
                    }}
                    className={`shrink-0 mt-0.5 ${isEditing ? "opacity-30 cursor-not-allowed" : "cursor-grab active:cursor-grabbing opacity-40 group-hover:opacity-80"}`}
                    title={isEditing ? "" : "Drag to reorder"}
                    aria-label="Drag to reorder"
                    data-testid={`drag-handle-tag-${i}`}
                  >
                    <GripVertical className="h-3.5 w-3.5" />
                  </span>
                  {isEditing ? (
                    <textarea
                      ref={editTextareaRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={(e) => {
                        // Enter saves; Shift+Enter inserts a newline (but we trim so it
                        // collapses) — easier: Enter saves, Esc cancels, no newlines allowed.
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitEdit(); }
                        else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      }}
                      onFocus={(e) => e.currentTarget.select()}
                      rows={1}
                      className="min-w-0 flex-1 bg-transparent outline-none resize-none text-xs leading-snug rounded-md px-1.5 py-0.5 ring-1 ring-current/30 focus:ring-current/70"
                      data-testid={`input-edit-tag-${i}`}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(i)}
                      className="min-w-0 flex-1 break-words whitespace-normal text-left cursor-text hover:underline decoration-dotted underline-offset-2"
                      title="Click to edit"
                      data-testid={`button-edit-tag-${i}`}
                    >
                      {v}
                    </button>
                  )}
                  {!isEditing && (
                    <Pencil
                      className="h-3 w-3 shrink-0 mt-0.5 opacity-0 group-hover:opacity-60 transition-opacity"
                      aria-hidden
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeTag(i)}
                    className="shrink-0 mt-0.5 hover:opacity-70"
                    aria-label={`Remove ${v}`}
                    data-testid={`button-remove-tag-${i}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
          {/* Trailing drop zone — lets you drop at the very bottom */}
          {dragIdx !== null && (
            <div
              className="relative h-2"
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setInsertAt(values.length);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIdx !== null) reorder(dragIdx, values.length);
                setDragIdx(null);
                setInsertAt(null);
              }}
            >
              <div
                aria-hidden
                className={`absolute top-0 left-0 right-0 h-0.5 rounded-full transition-opacity ${indicatorColor} ${
                  showIndicatorAfterLast() ? "opacity-100" : "opacity-0"
                }`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

function LogoUpload({ logoUrl, onUpload, onRemove }: { logoUrl: string; onUpload: (url: string) => void; onRemove: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please upload an image file.", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum size is 10MB.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).message || "Upload failed");
      const data = await res.json();
      onUpload(data.url || data.falUrl);
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  if (logoUrl) {
    return (
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 rounded-xl border border-border overflow-hidden bg-muted shrink-0">
          <img src={logoUrl} alt="Brand logo" className="w-full h-full object-contain" data-testid="img-brand-logo" />
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Logo uploaded</p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full text-xs gap-1"
              onClick={() => fileRef.current?.click()}
              data-testid="button-replace-logo"
            >
              <Upload className="h-3 w-3" /> Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full text-xs gap-1 text-destructive hover:text-destructive"
              onClick={onRemove}
              data-testid="button-remove-logo"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </Button>
          </div>
        </div>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ""; }} />
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/60 hover:border-primary/40 bg-muted/30 transition-colors cursor-pointer p-6"
      onClick={() => fileRef.current?.click()}
      onDragOver={e => e.preventDefault()}
      onDrop={handleDrop}
      data-testid="dropzone-logo"
    >
      {uploading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          Uploading...
        </div>
      ) : (
        <>
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm font-medium">Drop your logo here or click to upload</p>
          <p className="text-[11px] text-muted-foreground">PNG, JPG, GIF, or WebP up to 10MB</p>
        </>
      )}
      <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
}

// Master-only internal client classification (moved here from the Admin page's
// "Client Categories" tab). Values mirror BRAND_CLIENT_CATEGORIES in
// shared/schema.ts; a label only — no behavior changes anywhere else.
const CLIENT_CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "trial_users", label: "Trial Users" },
  { value: "demos", label: "Demos" },
  { value: "current_clients", label: "Current Clients" },
  { value: "smb_automated", label: "SMB" },
  { value: "previous_clients", label: "Previous Clients" },
];

// Rendered ONLY for masters editing an existing brand. Saves through the
// master-only PATCH /api/admin/brands/:id/client-category endpoint, deliberately
// separate from the regular brand-save payload so the general update route's
// clientCategory stripping for non-masters stays untouched. The server strips
// clientCategory from brand payloads for non-masters, so this hydrates only for
// masters (and is never rendered for anyone else).
function ClientCategoryField({ brandId }: { brandId: string }) {
  const { toast } = useToast();

  // Same query key as the wizard's existingBrand query — served from cache.
  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  const save = useMutation({
    mutationFn: async (clientCategory: string | null) => {
      const res = await apiRequest("PATCH", `/api/admin/brands/${brandId}/client-category`, { clientCategory });
      return res.json() as Promise<Brand>;
    },
    onSuccess: (_data, clientCategory) => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brands"] });
      // The sidebar brand list uses "/api/brands?workspaceId=..." string keys —
      // prefix-match so the grouped brand popup refreshes immediately.
      queryClient.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/brands"),
      });
      const label = CLIENT_CATEGORY_OPTIONS.find((o) => o.value === clientCategory)?.label;
      toast({ title: label ? `Client category set to ${label}` : "Client category cleared" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to save client category", description: e.message, variant: "destructive" });
    },
  });

  const current = (brand as { clientCategory?: string | null } | undefined)?.clientCategory ?? "__none__";

  return (
    <div className="space-y-2 border-t border-border/60 pt-4">
      <div className="flex items-center gap-2">
        <FieldLabel>Client Category</FieldLabel>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Internal · masters only</Badge>
        {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      <p className="text-[11px] text-muted-foreground -mt-1">
        Internal bookkeeping — groups brands in the sidebar brand switcher for masters. Saves immediately;
        not related to the market "Category" field above.
      </p>
      <Select
        value={current}
        onValueChange={(v) => save.mutate(v === "__none__" ? null : v)}
        disabled={save.isPending}
      >
        <SelectTrigger data-testid="select-client-category" className="w-full max-w-xs">
          <SelectValue placeholder="None (Uncategorized)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None (Uncategorized)</SelectItem>
          {CLIENT_CATEGORY_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StepBasics({ form, update, editBrandId }: { form: BrandFormData; update: (patch: Partial<BrandFormData>) => void; editBrandId?: string }) {
  const { user } = useAuth();
  const { enabled, brands } = useBrandUrlNames();
  const [, navigateUrlName] = useLocation();
  const urlBrand = brands.find(b => b.id === editBrandId);
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Brand Basics</h2>
            <p className="text-sm text-muted-foreground">Start with the essentials — what is this brand called and what does it do?</p>
          </div>
        </div>
      </div>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel required>Name</FieldLabel>
          <Input
            data-testid="input-brand-name"
            value={form.name}
            onChange={e => update({ name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") })}
            placeholder="e.g. Papabear"
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div className="space-y-2">
            <FieldLabel>Category</FieldLabel>
            <Input
              data-testid="input-brand-category"
              value={form.category}
              onChange={e => update({ category: e.target.value })}
              placeholder="e.g. DTC Skincare"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>URL</FieldLabel>
            <Input
              type="url"
              data-testid="input-brand-url"
              value={form.url}
              onChange={e => update({ url: e.target.value })}
              placeholder="e.g. https://papabear.com"
            />
          </div>
        </div>
        <div className="space-y-2">
          <FieldLabel>Summary</FieldLabel>
          <Textarea
            data-testid="input-brand-summary"
            value={form.summary}
            onChange={e => update({ summary: e.target.value })}
            placeholder="Brief description of the brand..."
            rows={3}
          />
        </div>
        <div className="space-y-2">
          <FieldLabel>Social Profiles</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Add a row per profile — paste a URL and we'll auto-detect the platform.</p>
          <SocialProfilesEditor value={form.socialProfiles} onChange={v => update({ socialProfiles: v })} />
        </div>
        {editBrandId && user?.isMaster && <ClientCategoryField brandId={editBrandId} />}
        {editBrandId && enabled && canEditBrandUrlName(user) && urlBrand && <BrandUrlNameField
          brandId={editBrandId}
          urlName={urlBrand.urlName}
          onSaved={brand => {
            reconcileBrandUrlNameCache(queryClient, brand);
            const suffix = window.location.pathname.replace(/^\/brands\/[^/]+/, "");
            navigateUrlName(brandPath(brand, suffix + window.location.search + window.location.hash, { eligible: true }), { replace: true });
          }}
        />}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel>Logo</FieldLabel>
          <LogoUpload
            logoUrl={form.logoUrl}
            onUpload={(url) => update({ logoUrl: url })}
            onRemove={() => update({ logoUrl: "" })}
          />
        </div>
        <div className="space-y-2">
          <FieldLabel>AdGen Logo</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">A separate logo optimized for AI image generation (e.g. transparent PNG, high contrast)</p>
          <LogoUpload
            logoUrl={form.adgenLogoUrl}
            onUpload={(url) => update({ adgenLogoUrl: url })}
            onRemove={() => update({ adgenLogoUrl: "" })}
          />
        </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6 space-y-3">
          <FieldLabel>Color Palette</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Define your brand colors with a label and hex value (up to 6)</p>
          <div className="space-y-2">
            {form.colorPalette.map((entry, i) => (
              <div key={i} className="flex items-center gap-2">
                <label
                  className="relative w-8 h-8 rounded-md border border-border/50 shrink-0 cursor-pointer overflow-hidden hover-elevate"
                  style={{
                    backgroundColor: entry.hex || "transparent",
                    backgroundImage: entry.hex
                      ? undefined
                      : "conic-gradient(from 0deg, #ef4444, #f59e0b, #eab308, #22c55e, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #ef4444)",
                  }}
                  title="Click to pick a color"
                  aria-label="Pick a color"
                  data-testid={`swatch-color-${i}`}
                >
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(entry.hex) ? entry.hex : "#000000"}
                    onChange={e => {
                      const updated = [...form.colorPalette];
                      updated[i] = { ...updated[i], hex: e.target.value.toUpperCase() };
                      update({ colorPalette: updated });
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid={`input-color-picker-${i}`}
                  />
                </label>
                <Input
                  data-testid={`input-color-label-${i}`}
                  value={entry.label}
                  onChange={e => {
                    const updated = [...form.colorPalette];
                    updated[i] = { ...updated[i], label: e.target.value };
                    update({ colorPalette: updated });
                  }}
                  placeholder="e.g. Primary — Navy Blue"
                  className="flex-1"
                />
                <Input
                  data-testid={`input-color-hex-${i}`}
                  value={entry.hex}
                  onChange={e => {
                    const updated = [...form.colorPalette];
                    updated[i] = { ...updated[i], hex: e.target.value };
                    update({ colorPalette: updated });
                  }}
                  placeholder="#3F5094"
                  className="w-28"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    const updated = form.colorPalette.filter((_, j) => j !== i);
                    update({ colorPalette: updated });
                  }}
                  data-testid={`button-remove-color-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
          {form.colorPalette.length < 6 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => update({ colorPalette: [...form.colorPalette, { label: "", hex: "" }] })}
              data-testid="button-add-color"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Color
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StepStrategic({ form, update }: { form: BrandFormData; update: (patch: Partial<BrandFormData>) => void }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Target className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Strategic DNA</h2>
          <p className="text-sm text-muted-foreground">Define the beliefs, positioning, and narrative foundation of your brand.</p>
        </div>
      </div>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel>Positioning</FieldLabel>
          <Textarea data-testid="input-brand-positioning" value={form.positioning} onChange={e => update({ positioning: e.target.value })} placeholder="How the brand positions itself in the market..." rows={3} />
        </div>
        <div className="space-y-2">
          <FieldLabel>Worldview</FieldLabel>
          <Textarea data-testid="input-brand-worldview" value={form.worldview} onChange={e => update({ worldview: e.target.value })} placeholder="The brand's perspective on the world..." rows={3} />
        </div>
        <div className="space-y-2">
          <FieldLabel>Enemy</FieldLabel>
          <Textarea data-testid="input-brand-enemy" value={form.enemy} onChange={e => update({ enemy: e.target.value })} placeholder="What the brand stands against..." rows={2} />
        </div>
        <div className="space-y-2">
          <FieldLabel>Promise</FieldLabel>
          <Textarea data-testid="input-brand-promise" value={form.promise} onChange={e => update({ promise: e.target.value })} placeholder="The core promise to customers..." rows={2} />
        </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel>Founder Story</FieldLabel>
          <Textarea data-testid="input-brand-founder-story" value={form.founderStory} onChange={e => update({ founderStory: e.target.value })} placeholder="The origin story of the brand..." rows={4} />
        </div>
        <div className="space-y-2">
          <FieldLabel>Mission</FieldLabel>
          <Textarea data-testid="input-brand-mission" value={form.mission} onChange={e => update({ mission: e.target.value })} placeholder="The brand's mission statement..." rows={2} />
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StepTone({ form, update }: { form: BrandFormData; update: (patch: Partial<BrandFormData>) => void }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Volume2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Tone & Voice</h2>
          <p className="text-sm text-muted-foreground">Define how the brand sounds — its personality, language choices, and verbal identity.</p>
        </div>
      </div>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel>Tone Summary</FieldLabel>
          <Textarea data-testid="input-brand-tone" value={form.toneSummary} onChange={e => update({ toneSummary: e.target.value })} placeholder="Describe the brand's voice and tone..." rows={4} />
        </div>
        <div className="space-y-2">
          <FieldLabel>Approved Phrases</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Words and phrases the brand should use</p>
          <TagInput values={form.approvedPhrases} onChange={v => update({ approvedPhrases: v })} placeholder="Add approved phrase..." accentColor="emerald" />
        </div>
        <div className="space-y-2">
          <FieldLabel>Banned Phrases</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Words and phrases the brand must avoid</p>
          <TagInput values={form.bannedPhrases} onChange={v => update({ bannedPhrases: v })} placeholder="Add banned phrase..." accentColor="red" />
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StepGuardrails({ form, update }: { form: BrandFormData; update: (patch: Partial<BrandFormData>) => void }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Guardrails & Rules</h2>
          <p className="text-sm text-muted-foreground">Set the boundaries, proof standards, and non-negotiable truths for your brand.</p>
        </div>
      </div>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel>Proof Hierarchy</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Ordered list of acceptable proof types (most credible first)</p>
          <TagInput values={form.proofHierarchy} onChange={v => update({ proofHierarchy: v })} placeholder="Add proof type..." accentColor="primary" />
        </div>
        <div className="space-y-2">
          <FieldLabel>Claim Guardrails</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Rules about what claims the brand can and cannot make</p>
          <TagInput values={form.claimGuardrails} onChange={v => update({ claimGuardrails: v })} placeholder="Add claim guardrail..." accentColor="destructive" />
        </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6 space-y-5">
        <div className="space-y-2">
          <FieldLabel>Visual Rules</FieldLabel>
          <Textarea data-testid="input-brand-visual-rules" value={form.visualRules} onChange={e => update({ visualRules: e.target.value })} placeholder="Visual identity guidelines, dos and don'ts..." rows={3} />
        </div>
        <div className="space-y-2">
          <FieldLabel>Non-Negotiable Truths</FieldLabel>
          <p className="text-[11px] text-muted-foreground -mt-1">Core truths that must never be contradicted</p>
          <TagInput values={form.nonNegotiableTruths} onChange={v => update({ nonNegotiableTruths: v })} placeholder="Add non-negotiable truth..." accentColor="amber" />
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

const RESEARCH_STAGES = [
  { from: 0, to: 30, label: "Discovering brand presence", detail: "Crawling the website and locating official sources" },
  { from: 30, to: 80, label: "Analyzing positioning & voice", detail: "Reading copy, tone, claims and competitive frame" },
  { from: 80, to: 180, label: "Mapping audience & products", detail: "Identifying target signals, key products and pricing" },
  { from: 180, to: 300, label: "Synthesizing brand intelligence", detail: "Compiling proofs, guardrails and category language" },
  { from: 300, to: 600, label: "Finalizing & cross-checking", detail: "Validating sources and confidence" },
];

function AIResearchProgress({ status, startedAt, errorMessage }: { status: "running" | "failed"; startedAt: string | Date | null | undefined; errorMessage: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== "running") return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [status]);

  const startMs = startedAt ? new Date(startedAt).getTime() : now;
  const elapsedSec = Math.max(0, Math.floor((now - startMs) / 1000));
  const minutes = Math.floor(elapsedSec / 60);
  const seconds = elapsedSec % 60;
  const elapsedLabel = `${minutes}m ${seconds.toString().padStart(2, "0")}s`;

  // Notional progress: caps at 95% so we never look "done" until status flips
  const expectedTotalSec = 360;
  const pct = status === "running" ? Math.min(95, Math.round((elapsedSec / expectedTotalSec) * 100)) : 100;

  const currentStageIdx = RESEARCH_STAGES.findIndex((s) => elapsedSec >= s.from && elapsedSec < s.to);
  const stageIdx = currentStageIdx === -1 ? RESEARCH_STAGES.length - 1 : currentStageIdx;

  if (status === "failed") {
    return (
      <Card className="mb-6 border-destructive/40 bg-destructive/5" data-testid="ai-research-failed-card">
        <CardContent className="p-5 flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
            <X className="h-4 w-4 text-destructive" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-destructive">AI Research failed</div>
            <div className="text-xs text-muted-foreground mt-1 break-words">
              {errorMessage || "Something went wrong. You can try again from the button above."}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="ai-research-progress-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-primary animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-semibold text-sm" data-testid="text-research-status-title">AI Research in progress</div>
              <div className="text-xs font-mono text-muted-foreground" data-testid="text-research-elapsed">{elapsedLabel} elapsed</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This typically takes <strong>4–6 minutes</strong>. Feel free to keep editing other fields, navigate away, or close this tab — we'll send a notification when it's done.
            </p>
          </div>
        </div>

        <div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              data-testid="progress-bar-research"
            />
          </div>
          <div className="mt-1.5 text-[11px] text-muted-foreground flex items-center justify-between">
            <span>{pct}%</span>
            <span>~{Math.max(1, Math.ceil((expectedTotalSec - elapsedSec) / 60))} min remaining</span>
          </div>
        </div>

        <div className="space-y-2">
          {RESEARCH_STAGES.map((stage, i) => {
            const done = i < stageIdx;
            const active = i === stageIdx;
            return (
              <div key={i} className="flex items-start gap-2.5" data-testid={`research-stage-${i}`}>
                <div className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-primary text-primary-foreground" : active ? "bg-primary/20 ring-2 ring-primary" : "bg-muted"}`}>
                  {done ? <Check className="h-2.5 w-2.5" /> : active ? <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> : null}
                </div>
                <div className="min-w-0">
                  <div className={`text-xs ${active ? "font-medium" : done ? "text-muted-foreground line-through" : "text-muted-foreground"}`}>{stage.label}</div>
                  {active && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{stage.detail}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

type SocialPlatformKey =
  | "instagram" | "tiktok" | "x" | "youtube" | "facebook"
  | "linkedin" | "pinterest" | "threads" | "snapchat" | "reddit" | "website";

const SOCIAL_PLATFORMS: Record<SocialPlatformKey, { label: string; icon: IconType; color: string; hosts: string[] }> = {
  instagram: { label: "Instagram", icon: SiInstagram, color: "#E4405F", hosts: ["instagram.com"] },
  tiktok:    { label: "TikTok",    icon: SiTiktok,    color: "#000000", hosts: ["tiktok.com"] },
  x:         { label: "X",         icon: SiX,         color: "#000000", hosts: ["x.com", "twitter.com"] },
  youtube:   { label: "YouTube",   icon: SiYoutube,   color: "#FF0000", hosts: ["youtube.com", "youtu.be"] },
  facebook:  { label: "Facebook",  icon: SiFacebook,  color: "#1877F2", hosts: ["facebook.com", "fb.com"] },
  linkedin:  { label: "LinkedIn",  icon: SiLinkedin,  color: "#0A66C2", hosts: ["linkedin.com"] },
  pinterest: { label: "Pinterest", icon: SiPinterest, color: "#BD081C", hosts: ["pinterest.com"] },
  threads:   { label: "Threads",   icon: SiThreads,   color: "#000000", hosts: ["threads.net"] },
  snapchat:  { label: "Snapchat",  icon: SiSnapchat,  color: "#000000", hosts: ["snapchat.com"] },
  reddit:    { label: "Reddit",    icon: SiReddit,    color: "#FF4500", hosts: ["reddit.com"] },
  website:   { label: "Website",   icon: Globe as unknown as IconType, color: "#6B7280", hosts: [] },
};

function isParseableHttpUrl(value: string): boolean {
  if (!value) return false;
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function detectPlatform(url: string): SocialPlatformKey {
  if (!url) return "website";
  const trimmed = url.trim();
  if (!trimmed) return "website";
  // Try to parse as a URL first so substring matches don't false-positive
  // on things like `notinstagram.com`.
  let hostname = "";
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    hostname = new URL(withProto).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    hostname = trimmed.toLowerCase();
  }
  for (const [key, def] of Object.entries(SOCIAL_PLATFORMS) as [SocialPlatformKey, typeof SOCIAL_PLATFORMS[SocialPlatformKey]][]) {
    if (def.hosts.some(h => hostname === h || hostname.endsWith(`.${h}`))) return key;
  }
  return "website";
}

function normalizePlatformName(raw: unknown): SocialPlatformKey {
  if (typeof raw !== "string") return "website";
  const k = raw.trim().toLowerCase();
  if (k in SOCIAL_PLATFORMS) return k as SocialPlatformKey;
  if (k === "twitter") return "x";
  if (k === "ig") return "instagram";
  if (k === "fb") return "facebook";
  if (k === "yt") return "youtube";
  return "website";
}

function handleToUrl(platform: SocialPlatformKey, handle: string): string {
  const clean = handle.replace(/^@/, "").trim();
  if (!clean) return "";
  switch (platform) {
    case "instagram": return `https://instagram.com/${clean}`;
    case "tiktok":    return `https://tiktok.com/@${clean}`;
    case "x":         return `https://x.com/${clean}`;
    case "youtube":   return clean.startsWith("UC") ? `https://youtube.com/channel/${clean}` : `https://youtube.com/@${clean}`;
    case "facebook":  return `https://facebook.com/${clean}`;
    case "linkedin":  return `https://linkedin.com/company/${clean}`;
    case "pinterest": return `https://pinterest.com/${clean}`;
    case "threads":   return `https://threads.net/@${clean}`;
    case "snapchat":  return `https://snapchat.com/add/${clean}`;
    case "reddit":    return `https://reddit.com/r/${clean}`;
    default:          return clean;
  }
}

function parseSocialProfiles(raw: string): { platform: SocialPlatformKey; url: string }[] {
  if (!raw || !raw.trim()) return [];
  // Try JSON first.
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item: any) => {
          if (!item) return null;
          if (typeof item === "string") {
            return { platform: detectPlatform(item), url: item };
          }
          const url = typeof item.url === "string" ? item.url
            : typeof item.handle === "string" ? item.handle
            : typeof item.value === "string" ? item.value : "";
          const platformHint = item.platform ?? item.network ?? item.type;
          const platform = platformHint
            ? normalizePlatformName(platformHint)
            : detectPlatform(url);
          // If the value is a bare handle (no http), build a real URL.
          const finalUrl = /^https?:\/\//i.test(url) || !url ? url : handleToUrl(platform, url);
          return { platform, url: finalUrl };
        })
        .filter(Boolean) as { platform: SocialPlatformKey; url: string }[];
    }
    if (parsed && typeof parsed === "object") {
      return Object.entries(parsed).map(([k, v]) => {
        const platform = normalizePlatformName(k);
        const handle = typeof v === "string" ? v : "";
        const url = /^https?:\/\//i.test(handle) || !handle ? handle : handleToUrl(platform, handle);
        return { platform, url };
      });
    }
  } catch {
    // Fall through to freeform parsing.
  }
  // Freeform: split by newline / comma, treat each entry as a URL.
  return raw
    .split(/[\n,]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => ({ platform: detectPlatform(s), url: s }));
}

function serializeSocialProfiles(rows: { platform: SocialPlatformKey; url: string }[]): string {
  const cleaned = rows
    .map(r => ({ platform: SOCIAL_PLATFORMS[r.platform].label, url: r.url.trim() }))
    .filter(r => r.url.length > 0);
  return cleaned.length ? JSON.stringify(cleaned, null, 2) : "";
}

function SocialProfilesEditor({ value, onChange }: { value: string; onChange: (json: string) => void }) {
  const [rows, setRows] = useState<{ platform: SocialPlatformKey; url: string }[]>(() => parseSocialProfiles(value));
  const lastSerializedRef = useRef<string>(value);

  // Re-parse if the parent value changes from outside (e.g. AI research populates it),
  // but ignore echoes of our own serialization.
  useEffect(() => {
    if (value === lastSerializedRef.current) return;
    setRows(parseSocialProfiles(value));
    lastSerializedRef.current = value;
  }, [value]);

  const commit = (next: { platform: SocialPlatformKey; url: string }[]) => {
    setRows(next);
    const serialized = serializeSocialProfiles(next);
    lastSerializedRef.current = serialized;
    onChange(serialized);
  };

  const updateRow = (idx: number, patch: Partial<{ platform: SocialPlatformKey; url: string }>) => {
    const next = rows.map((r, i) => {
      if (i !== idx) return r;
      const merged = { ...r, ...patch };
      // Auto-detect platform when URL changes and the platform was still the
      // generic "website" fallback (so we don't override an explicit pick).
      if (patch.url !== undefined && r.platform === "website") {
        const detected = detectPlatform(patch.url);
        if (detected !== "website") merged.platform = detected;
      }
      return merged;
    });
    commit(next);
  };

  const addRow = () => commit([...rows, { platform: "website", url: "" }]);
  const removeRow = (idx: number) => commit(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid="editor-social-profiles">
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          No social profiles yet. Add one below.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row, idx) => {
            const def = SOCIAL_PLATFORMS[row.platform];
            const Icon = def.icon;
            return (
              <div
                key={idx}
                className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 hover-elevate"
                data-testid={`row-social-profile-${idx}`}
              >
                <div
                  className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center"
                  style={{ backgroundColor: `${def.color}15`, color: def.color }}
                  title={def.label}
                  data-testid={`icon-platform-${row.platform}-${idx}`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <select
                  value={row.platform}
                  onChange={e => updateRow(idx, { platform: e.target.value as SocialPlatformKey })}
                  className="h-8 rounded-md border bg-background px-2 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid={`select-platform-${idx}`}
                >
                  {(Object.keys(SOCIAL_PLATFORMS) as SocialPlatformKey[]).map(k => (
                    <option key={k} value={k}>{SOCIAL_PLATFORMS[k].label}</option>
                  ))}
                </select>
                <Input
                  value={row.url}
                  onChange={e => updateRow(idx, { url: e.target.value })}
                  placeholder={row.platform === "website" ? "https://..." : `https://${def.hosts[0] ?? "example.com"}/handle`}
                  className="h-8 flex-1 text-xs"
                  data-testid={`input-social-url-${idx}`}
                />
                {row.url && /^https?:\/\//i.test(row.url) && (
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noreferrer"
                    className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center text-muted-foreground hover-elevate"
                    title="Open in new tab"
                    aria-label={`Open ${def.label} profile in a new tab`}
                    data-testid={`link-open-social-${idx}`}
                  >
                    <Globe className="h-3.5 w-3.5" />
                  </a>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeRow(idx)}
                  title="Remove profile"
                  aria-label={`Remove ${def.label} profile`}
                  data-testid={`button-remove-social-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="w-full"
        data-testid="button-add-social-profile"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add social profile
      </Button>
    </div>
  );
}

function StepDesignSystem({ form, update, brandId }: { form: BrandFormData; update: (patch: Partial<BrandFormData>) => void; brandId?: string }) {
  // Task #1529 — the CTA/button style editor is gated behind the BRAND-LEVEL
  // `brand_cta_style` beta flag (the endpoint reports it ON when the feature
  // is enabled for the brand on the Ads Express admin Features tab / Admin →
  // Beta Features). Only checkable once the brand exists (edit mode, or right
  // after creation); during initial creation there's no brand yet, so the
  // section stays hidden (fail-closed) — same rule as BrandDesignSystemTab.
  const { data: betaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId,
  });
  const showCtaStyle = betaFlags?.brand_cta_style === true;
  return (
    <Card className="border-0 shadow-none bg-transparent">
      <CardContent className="p-0 space-y-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Design System</h2>
          <p className="text-xs text-muted-foreground mt-1">
            The visual source of truth for landing pages, ads, and image prompts. Optional — you can fill this in later.
          </p>
        </div>
        <BrandDesignSystemEditor
          value={form.brandDesignSystem}
          onChange={(next) => update({ brandDesignSystem: next })}
          showCtaStyle={showCtaStyle}
        />
      </CardContent>
    </Card>
  );
}

function StepAdvanced({ form, update }: { form: BrandFormData; update: (patch: Partial<BrandFormData>) => void }) {
  const { user } = useAuth();
  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Database className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Advanced (Optional)</h2>
          <p className="text-sm text-muted-foreground">Extra brand intelligence — most useful when populated by AI Research.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="space-y-2">
            <FieldLabel>Price Positioning</FieldLabel>
            <Textarea data-testid="input-brand-price-positioning" value={form.pricePositioning} onChange={e => update({ pricePositioning: e.target.value })} placeholder="e.g. Premium, value, luxury, mid-market…" rows={2} />
          </div>
          <div className="space-y-2">
            <FieldLabel>Strategic Priorities</FieldLabel>
            <p className="text-[11px] text-muted-foreground -mt-1">High-level business priorities driving the brand</p>
            <TagInput values={form.strategicPriorities} onChange={v => update({ strategicPriorities: v })} placeholder="Add a strategic priority..." accentColor="primary" />
          </div>
          <div className="space-y-2">
            <FieldLabel>Target Audience Signals</FieldLabel>
            <p className="text-[11px] text-muted-foreground -mt-1">Observable cues about who buys and why</p>
            <TagInput values={form.targetAudienceSignals} onChange={v => update({ targetAudienceSignals: v })} placeholder="Add an audience signal..." accentColor="primary" />
          </div>
          <div className="space-y-2">
            <FieldLabel>Key Products / Lines</FieldLabel>
            <p className="text-[11px] text-muted-foreground -mt-1">The brand's notable products, SKUs or product lines</p>
            <TagInput values={form.keyProducts} onChange={v => update({ keyProducts: v })} placeholder="Add a key product..." accentColor="emerald" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="space-y-2">
            <FieldLabel>Category Language</FieldLabel>
            <p className="text-[11px] text-muted-foreground -mt-1">Vocabulary commonly used in this category</p>
            <TagInput values={form.categoryLanguage} onChange={v => update({ categoryLanguage: v })} placeholder="Add category term..." accentColor="primary" />
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card data-testid="card-admin-research-metadata">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2 -mb-1">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Admin only</Badge>
              <span className="text-xs text-muted-foreground">Internal AI research metadata, not shown to standard users.</span>
            </div>
            <div className="space-y-2">
              <FieldLabel>Research Sources</FieldLabel>
              <p className="text-[11px] text-muted-foreground -mt-1">URLs the AI used or that support these brand facts</p>
              <TagInput values={form.researchSources} onChange={v => update({ researchSources: v })} placeholder="https://..." accentColor="primary" />
            </div>
            <div className="space-y-2">
              <FieldLabel>Research Notes / Caveats</FieldLabel>
              <Textarea data-testid="input-brand-research-notes" value={form.researchNotes} onChange={e => update({ researchNotes: e.target.value })} placeholder="Any caveats or uncertainty around the data above..." rows={3} />
            </div>
            <div className="space-y-2">
              <FieldLabel>Research Confidence (0–100)</FieldLabel>
              <Input
                type="number"
                min={0}
                max={100}
                data-testid="input-brand-research-confidence"
                value={form.researchConfidence ?? ""}
                onChange={e => {
                  const v = e.target.value;
                  if (v === "") update({ researchConfidence: null });
                  else {
                    const n = parseInt(v, 10);
                    update({ researchConfidence: isNaN(n) ? null : Math.max(0, Math.min(100, n)) });
                  }
                }}
                placeholder="e.g. 85"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReviewSection({ icon: Icon, title, children, empty }: {
  icon: typeof Building2; title: string; children?: React.ReactNode; empty?: boolean;
}) {
  if (empty) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
          <Icon className="h-3 w-3 text-primary" />
        </div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function StepReview({ form, onGoToStep }: { form: BrandFormData; onGoToStep: (idx: number) => void }) {
  const hasStrategic = !!(form.positioning || form.worldview || form.enemy || form.promise || form.founderStory || form.mission);
  const hasTone = !!(form.toneSummary || hasBrandValue(form.approvedPhrases) || hasBrandValue(form.bannedPhrases));
  const hasGuardrails = !!(hasBrandValue(form.proofHierarchy) || hasBrandValue(form.claimGuardrails) || form.visualRules || hasBrandValue(form.nonNegotiableTruths));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Eye className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight" data-testid="text-step-title">Review & Create</h2>
          <p className="text-sm text-muted-foreground">Review your brand profile before creating it. Click any section to edit.</p>
        </div>
      </div>

      <Card className="border-border/40">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                <Building2 className="h-3 w-3 text-primary" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Basics</p>
            </div>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => onGoToStep(0)} data-testid="button-edit-basics">Edit</Button>
          </div>
          <div className="pl-7 space-y-2">
            {form.logoUrl && (
              <div className="flex items-center gap-2 mb-1">
                <div className="w-10 h-10 rounded-lg border border-border overflow-hidden bg-muted">
                  <img src={form.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <span className="text-xs text-muted-foreground">Logo</span>
              </div>
            )}
            {form.adgenLogoUrl && (
              <div className="flex items-center gap-2 mb-1">
                <div className="w-10 h-10 rounded-lg border border-border overflow-hidden bg-muted">
                  <img src={form.adgenLogoUrl} alt="AdGen Logo" className="w-full h-full object-contain" />
                </div>
                <span className="text-xs text-muted-foreground">AdGen Logo</span>
              </div>
            )}
            <div>
              <span className="text-xs text-muted-foreground">Name: </span>
              <span className="text-sm font-medium">{form.name || "—"}</span>
            </div>
            {form.category && (
              <div>
                <span className="text-xs text-muted-foreground">Category: </span>
                <span className="text-sm">{form.category}</span>
              </div>
            )}
            {form.summary && (
              <div>
                <span className="text-xs text-muted-foreground">Summary: </span>
                <span className="text-sm">{form.summary}</span>
              </div>
            )}
            {form.colorPalette.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground block mb-2">Color Palette</span>
                <div className="flex flex-wrap gap-3">
                  {form.colorPalette.map((c, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`review-color-${i}`}>
                      <div className="w-6 h-6 rounded border border-border/50" style={{ backgroundColor: c.hex || "#ccc" }} />
                      <span className="text-xs">{c.label}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">{c.hex}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {hasStrategic && (
        <Card className="border-border/40">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                  <Target className="h-3 w-3 text-primary" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Strategic DNA</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => onGoToStep(1)} data-testid="button-edit-strategic">Edit</Button>
            </div>
            <div className="pl-7 space-y-3">
              {form.positioning && <div><span className="text-xs text-muted-foreground block mb-0.5">Positioning</span><p className="text-sm leading-relaxed">{form.positioning}</p></div>}
              {form.worldview && <div><span className="text-xs text-muted-foreground block mb-0.5">Worldview</span><p className="text-sm leading-relaxed">{form.worldview}</p></div>}
              {form.enemy && <div><span className="text-xs text-muted-foreground block mb-0.5">Enemy</span><p className="text-sm leading-relaxed">{form.enemy}</p></div>}
              {form.promise && <div><span className="text-xs text-muted-foreground block mb-0.5">Promise</span><p className="text-sm leading-relaxed">{form.promise}</p></div>}
              {form.founderStory && <div><span className="text-xs text-muted-foreground block mb-0.5">Founder Story</span><p className="text-sm leading-relaxed">{form.founderStory}</p></div>}
              {form.mission && <div><span className="text-xs text-muted-foreground block mb-0.5">Mission</span><p className="text-sm leading-relaxed">{form.mission}</p></div>}
            </div>
          </CardContent>
        </Card>
      )}

      {hasTone && (
        <Card className="border-border/40">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                  <Volume2 className="h-3 w-3 text-primary" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tone & Voice</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => onGoToStep(2)} data-testid="button-edit-tone">Edit</Button>
            </div>
            <div className="pl-7 space-y-3">
              {form.toneSummary && <div><span className="text-xs text-muted-foreground block mb-0.5">Tone Summary</span><p className="text-sm leading-relaxed">{form.toneSummary}</p></div>}
              {hasBrandValue(form.approvedPhrases) && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Approved Phrases</span>
                  <div className="flex flex-wrap gap-1.5">
                    {reviewEntries(form.approvedPhrases).map((p, i) => (
                      <Badge key={i} variant="secondary" className="text-xs rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0 px-3 py-1">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {hasBrandValue(form.bannedPhrases) && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Banned Phrases</span>
                  <div className="flex flex-wrap gap-1.5">
                    {reviewEntries(form.bannedPhrases).map((p, i) => (
                      <Badge key={i} variant="secondary" className="text-xs rounded-full bg-red-500/10 text-red-700 dark:text-red-400 border-0 px-3 py-1">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {hasGuardrails && (
        <Card className="border-border/40">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                  <Shield className="h-3 w-3 text-primary" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Guardrails & Rules</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => onGoToStep(3)} data-testid="button-edit-guardrails">Edit</Button>
            </div>
            <div className="pl-7 space-y-3">
              {hasBrandValue(form.proofHierarchy) && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Proof Hierarchy</span>
                  <ol className="space-y-1">
                    {reviewEntries(form.proofHierarchy).map((p, i) => (
                      <li key={i} className="text-sm flex gap-2"><span className="text-muted-foreground text-xs mt-0.5">{i + 1}.</span> {p}</li>
                    ))}
                  </ol>
                </div>
              )}
              {hasBrandValue(form.claimGuardrails) && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Claim Guardrails</span>
                  <ul className="space-y-1">
                    {reviewEntries(form.claimGuardrails).map((g, i) => (
                      <li key={i} className="text-sm flex gap-2"><AlertTriangle className="h-3 w-3 text-destructive mt-1 shrink-0" /> {g}</li>
                    ))}
                  </ul>
                </div>
              )}
              {form.visualRules && <div><span className="text-xs text-muted-foreground block mb-0.5">Visual Rules</span><p className="text-sm leading-relaxed">{form.visualRules}</p></div>}
              {hasBrandValue(form.nonNegotiableTruths) && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Non-Negotiable Truths</span>
                  <ul className="space-y-1">
                    {reviewEntries(form.nonNegotiableTruths).map((t, i) => (
                      <li key={i} className="text-sm flex gap-2"><Shield className="h-3 w-3 text-amber-500 mt-1 shrink-0" /> {t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(() => {
        const ds = form.brandDesignSystem as any;
        const hasDesign = !!(
          ds?.designPositioning ||
          (ds?.visualMood?.length ?? 0) ||
          (ds?.primaryColors?.length ?? 0) ||
          (ds?.secondaryColors?.length ?? 0) ||
          (ds?.accentColors?.length ?? 0) ||
          ds?.typographyDirection?.headlineStyle ||
          ds?.typographyDirection?.bodyStyle ||
          ds?.typographyDirection?.accentStyle ||
          (ds?.fonts?.length ?? 0) ||
          ds?.layoutDirection?.overallStyle ||
          ds?.layoutDirection?.sectionDensity ||
          (ds?.layoutDirection?.preferredLayouts?.length ?? 0) ||
          (ds?.layoutDirection?.avoidLayouts?.length ?? 0)
        );
        if (!hasDesign) return null;
        const allColors: string[] = [
          ...(ds?.primaryColors ?? []),
          ...(ds?.secondaryColors ?? []),
          ...(ds?.accentColors ?? []),
        ];
        return (
          <Card className="border-border/40">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                    <Palette className="h-3 w-3 text-primary" />
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Design System</p>
                </div>
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => onGoToStep(4)} data-testid="button-edit-design">Edit</Button>
              </div>
              <div className="pl-7 space-y-3">
                {ds?.designPositioning && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-0.5">Design Positioning</span>
                    <p className="text-sm leading-relaxed">{ds.designPositioning}</p>
                  </div>
                )}
                {(ds?.visualMood?.length ?? 0) > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Visual Mood</span>
                    <div className="flex flex-wrap gap-1.5">
                      {ds.visualMood.map((m: string, i: number) => (
                        <Badge key={i} variant="secondary" className="text-[11px]">{m}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {allColors.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Color Palette</span>
                    <div className="flex flex-wrap gap-1.5">
                      {allColors.map((hex, i) => (
                        <div key={i} className="flex items-center gap-1.5 rounded-md border border-border/40 px-2 py-1">
                          <div className="w-4 h-4 rounded border border-border/40" style={{ backgroundColor: hex || "#ccc" }} />
                          <span className="text-[11px] font-mono text-muted-foreground">{hex}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(ds?.typographyDirection?.headlineStyle || ds?.typographyDirection?.bodyStyle || ds?.typographyDirection?.accentStyle) && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Typography Direction</span>
                    <div className="space-y-1">
                      {ds.typographyDirection.headlineStyle && <p className="text-sm"><span className="text-xs text-muted-foreground">Headline: </span>{ds.typographyDirection.headlineStyle}</p>}
                      {ds.typographyDirection.bodyStyle && <p className="text-sm"><span className="text-xs text-muted-foreground">Body: </span>{ds.typographyDirection.bodyStyle}</p>}
                      {ds.typographyDirection.accentStyle && <p className="text-sm"><span className="text-xs text-muted-foreground">Accent: </span>{ds.typographyDirection.accentStyle}</p>}
                    </div>
                  </div>
                )}
                {(ds?.layoutDirection?.overallStyle || ds?.layoutDirection?.sectionDensity || (ds?.layoutDirection?.preferredLayouts?.length ?? 0) || (ds?.layoutDirection?.avoidLayouts?.length ?? 0)) && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Layout Direction</span>
                    <div className="space-y-1.5">
                      {ds.layoutDirection.overallStyle && <p className="text-sm"><span className="text-xs text-muted-foreground">Overall: </span>{ds.layoutDirection.overallStyle}</p>}
                      {ds.layoutDirection.sectionDensity && <p className="text-sm"><span className="text-xs text-muted-foreground">Density: </span>{ds.layoutDirection.sectionDensity}</p>}
                      {(ds.layoutDirection.preferredLayouts?.length ?? 0) > 0 && (
                        <div>
                          <span className="text-xs text-muted-foreground block mb-1">Preferred</span>
                          <div className="flex flex-wrap gap-1.5">
                            {ds.layoutDirection.preferredLayouts.map((l: string, i: number) => (
                              <Badge key={i} variant="secondary" className="text-[11px]">{l}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {(ds.layoutDirection.avoidLayouts?.length ?? 0) > 0 && (
                        <div>
                          <span className="text-xs text-muted-foreground block mb-1">Avoid</span>
                          <div className="flex flex-wrap gap-1.5">
                            {ds.layoutDirection.avoidLayouts.map((l: string, i: number) => (
                              <Badge key={i} variant="outline" className="text-[11px] border-destructive/40 text-destructive">{l}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {!hasStrategic && !hasTone && !hasGuardrails && (
        <Card className="border-dashed border-border/50">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground">Only basics have been filled in. You can always add more details later.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function IntakeScreen({
  name,
  url,
  logoUrl,
  logoMode,
  onChange,
  onSubmit,
  isSubmitting,
  errorMessage,
}: {
  name: string;
  url: string;
  logoUrl: string;
  logoMode: "upload" | "url";
  onChange: (patch: { name?: string; url?: string; logoUrl?: string; logoMode?: "upload" | "url" }) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  errorMessage?: string;
}) {
  const trimmedName = name.trim();
  const trimmedUrl = url.trim();
  const trimmedLogo = logoUrl.trim();
  const looksLikeUrl = isParseableHttpUrl(trimmedUrl);
  const logoLooksLikeUrl = isParseableHttpUrl(trimmedLogo);
  const canSubmit =
    trimmedName.length > 0 &&
    looksLikeUrl &&
    trimmedLogo.length > 0 &&
    (logoMode === "upload" || logoLooksLikeUrl) &&
    !isSubmitting;

  return (
    <div className="flex flex-col h-full bg-background" data-testid="brand-intake-screen">
      <div className="flex-1 overflow-auto">
        <div className="max-w-xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-intake-title">New Brand</h1>
              <p className="text-sm text-muted-foreground">
                Give us the basics. We'll research the rest with AI and walk you through the wizard to confirm.
              </p>
            </div>
          </div>

          <Card>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <FieldLabel required>Brand Name</FieldLabel>
                <Input
                  data-testid="input-intake-name"
                  value={name}
                  onChange={(e) => onChange({ name: e.target.value })}
                  placeholder="e.g. Kill Crew"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <FieldLabel required>Brand URL</FieldLabel>
                <Input
                  type="url"
                  data-testid="input-intake-url"
                  value={url}
                  onChange={(e) => onChange({ url: e.target.value })}
                  placeholder="https://killcrew.com"
                />
                {trimmedUrl.length > 0 && !looksLikeUrl && (
                  <p className="text-[11px] text-destructive">Enter a valid URL starting with http:// or https://</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FieldLabel required>Logo</FieldLabel>
                  <div className="inline-flex items-center rounded-full border border-border/60 bg-muted/30 p-0.5 text-[11px]">
                    <button
                      type="button"
                      onClick={() => onChange({ logoMode: "upload", logoUrl: "" })}
                      className={`px-3 py-1 rounded-full transition-colors ${logoMode === "upload" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                      data-testid="button-intake-logo-mode-upload"
                    >
                      Upload file
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange({ logoMode: "url", logoUrl: "" })}
                      className={`px-3 py-1 rounded-full transition-colors ${logoMode === "url" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                      data-testid="button-intake-logo-mode-url"
                    >
                      Paste URL
                    </button>
                  </div>
                </div>

                {logoMode === "upload" ? (
                  <LogoUpload
                    logoUrl={logoUrl}
                    onUpload={(u) => onChange({ logoUrl: u })}
                    onRemove={() => onChange({ logoUrl: "" })}
                  />
                ) : (
                  <div className="space-y-2">
                    <Input
                      type="url"
                      data-testid="input-intake-logo-url"
                      value={logoUrl}
                      onChange={(e) => onChange({ logoUrl: e.target.value })}
                      placeholder="https://example.com/logo.png"
                    />
                    {trimmedLogo.length > 0 && !logoLooksLikeUrl && (
                      <p className="text-[11px] text-destructive">Enter a valid image URL starting with http:// or https://</p>
                    )}
                    {trimmedLogo.length > 0 && logoLooksLikeUrl && (
                      <div className="flex items-center gap-3 mt-2">
                        <div className="relative w-16 h-16 rounded-xl border border-border overflow-hidden bg-muted shrink-0">
                          <img src={trimmedLogo} alt="Logo preview" className="w-full h-full object-contain" data-testid="img-intake-logo-preview" />
                        </div>
                        <p className="text-xs text-muted-foreground">Preview</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {errorMessage && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive" data-testid="text-intake-error">
                  {errorMessage}
                </div>
              )}

              <div className="pt-2 flex items-center justify-between gap-3">
                <p className="text-[11px] text-muted-foreground">
                  Research takes 4–6 minutes. We'll auto-fill the wizard when it's done.
                </p>
                <Button
                  type="button"
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  className="gap-1.5 px-6"
                  data-testid="button-intake-continue"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-background border-t-transparent rounded-full animate-spin" />
                      Starting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Continue
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ResearchingScreen({
  brandName,
  status,
  startedAt,
  errorMessage,
  onRetry,
  retryDisabled,
}: {
  brandName: string;
  status: "running" | "failed";
  startedAt: string | Date | null | undefined;
  errorMessage: string;
  onRetry: () => void;
  retryDisabled: boolean;
}) {
  return (
    <div className="flex flex-col h-full bg-background" data-testid="brand-researching-screen">
      <div className="flex-1 overflow-auto">
        <div className="max-w-xl mx-auto px-6 py-12">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight">{brandName || "New Brand"}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {status === "running"
                ? "Hang tight — Grok is researching your brand."
                : "AI research couldn't finish. You can try again below."}
            </p>
          </div>
          <AIResearchProgress status={status} startedAt={startedAt} errorMessage={errorMessage} />
          {status === "failed" && (
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={onRetry}
                disabled={retryDisabled}
                className="gap-1.5"
                data-testid="button-retry-research"
              >
                <Sparkles className="h-4 w-4" />
                {retryDisabled ? "Retrying…" : "Retry AI research"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BrandCreateAccessDenied() {
  const [, navigate] = useLocation();
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mb-5">
        <Shield className="h-7 w-7 text-destructive" />
      </div>
      <h2 className="text-xl font-semibold tracking-tight mb-1.5" data-testid="text-brand-create-denied">
        You don't have permission to create brands
      </h2>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed mb-6">
        Brand creation is limited to admins. Ask an admin to create the brand or grant you access.
      </p>
      <Button variant="outline" className="rounded-full px-6" onClick={() => navigate("/brands")} data-testid="button-back-to-brands">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Brands
      </Button>
    </div>
  );
}

export default function BrandWizardPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const params = useParams<{ brandId?: string }>();
  const editBrandId = params.brandId;
  const isEditMode = !!editBrandId;
  const { user, isLoading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<BrandFormData>({ ...EMPTY_FORM });
  const paletteEdited = useRef(false);
  const [loaded, setLoaded] = useState(false);
  // Tracks which AI-research result has already been merged into the form.
  // Seeded during hydration with the brand's existing research so a previously
  // completed run never re-merges over freshly hydrated (saved) values.
  const mergedResultIdRef = useRef<string | null>(null);

  // Create-flow only: tracks the brand row created from the intake screen so
  // we can poll its AI research status without navigating away from /brands/new.
  const [createdBrandId, setCreatedBrandId] = useState<string | null>(null);
  const [phase, setPhase] = useState<"intake" | "researching" | "wizard">(
    isEditMode ? "wizard" : "intake",
  );
  const [intake, setIntake] = useState<{
    name: string;
    url: string;
    logoUrl: string;
    logoMode: "upload" | "url";
  }>({ name: "", url: "", logoUrl: "", logoMode: "upload" });
  const [intakeError, setIntakeError] = useState<string>("");

  const effectiveBrandId = editBrandId || createdBrandId || undefined;

  const { data: workspaces } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });
  const workspaceId = workspaces?.[0]?.id;

  const { data: existingBrand } = useQuery<Brand>({
    queryKey: ["/api/brands", effectiveBrandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!effectiveBrandId,
    refetchInterval: (query) => {
      const data = query.state.data as any;
      return data?.aiResearchStatus === "running" ? 5000 : false;
    },
  });

  const aiStatus = (existingBrand as any)?.aiResearchStatus as string | undefined;
  const aiStartedAt = (existingBrand as any)?.aiResearchStartedAt as string | Date | null | undefined;
  const aiResearchError = (existingBrand as any)?.aiResearchError as string | null | undefined;
  const isResearching = aiStatus === "running";
  const aiResearchPartial =
    aiStatus === "completed" &&
    (existingBrand as any)?.aiResearchResult?.partial === true;

  // If the user lands on the wizard while a research is already running (e.g. tab reload),
  // resume tracking it in the global notifier so they get a toast on completion.
  useEffect(() => {
    if (isResearching && effectiveBrandId) {
      trackAIResearch(effectiveBrandId, (existingBrand as any)?.name || "Brand");
    }
  }, [isResearching, effectiveBrandId, existingBrand]);

  // Once Grok finishes (or fails) for the create flow, advance into the wizard.
  useEffect(() => {
    if (phase !== "researching") return;
    if (aiStatus === "completed") {
      setPhase("wizard");
      setCurrentStep(0);
    }
  }, [phase, aiStatus]);

  useEffect(() => {
    if (existingBrand && !loaded) {
      const vr = existingBrand.visualRules;
      setForm({
        name: existingBrand.name || "",
        slug: existingBrand.slug || "",
        category: existingBrand.category || "",
        url: (existingBrand as any).url || "",
        logoUrl: existingBrand.logoUrl || "",
        adgenLogoUrl: existingBrand.adgenLogoUrl || "",
        summary: existingBrand.summary || "",
        positioning: existingBrand.positioning || "",
        worldview: existingBrand.worldview || "",
        enemy: existingBrand.enemy || "",
        promise: existingBrand.promise || "",
        founderStory: existingBrand.founderStory || "",
        mission: existingBrand.mission || "",
        toneSummary: existingBrand.toneSummary || "",
        approvedPhrases: existingBrand.approvedPhrases,
        bannedPhrases: existingBrand.bannedPhrases,
        proofHierarchy: existingBrand.proofHierarchy,
        claimGuardrails: existingBrand.claimGuardrails,
        visualRules: typeof vr === "string" ? vr : (vr ? JSON.stringify(vr, null, 2) : ""),
        colorPalette: editablePalette(existingBrand.colorPalette),
        nonNegotiableTruths: existingBrand.nonNegotiableTruths,
        categoryLanguage: existingBrand.categoryLanguage,
        pricePositioning: (existingBrand as any).pricePositioning || "",
        targetAudienceSignals: existingBrand.targetAudienceSignals,
        keyProducts: existingBrand.keyProducts,
        socialProfiles: (() => {
          const sp = (existingBrand as any).socialProfiles;
          if (!sp) return "";
          if (typeof sp === "string") return sp;
          try { return JSON.stringify(sp, null, 2); } catch { return ""; }
        })(),
        researchSources: existingBrand.researchSources,
        researchNotes: (existingBrand as any).researchNotes || "",
        researchConfidence: (existingBrand as any).researchConfidence ?? null,
        productPhilosophy: "",
        officialUrl: "",
        strategicPriorities: existingBrand.strategicPriorities,
        brandDesignSystem: (() => {
          const v = (existingBrand as any).brandDesignSystem;
          return v && typeof v === "object" ? { ...emptyBrandDesignSystem(), ...v } : emptyBrandDesignSystem();
        })(),
      });
      // Mark the brand's already-completed research as merged so the AI-merge
      // effect doesn't clobber these freshly hydrated (saved) values on mount.
      // It only fires for a NEW research run that completes during this session.
      const initialCompletedAt = (existingBrand as any)?.aiResearchResult?.completedAt;
      if (initialCompletedAt) {
        mergedResultIdRef.current = `${effectiveBrandId}:${initialCompletedAt}`;
      }
      setLoaded(true);
    }
  }, [existingBrand, loaded]);

  const update = useCallback((patch: Partial<BrandFormData>) => {
    if ("colorPalette" in patch) paletteEdited.current = true;
    setForm(prev => ({ ...prev, ...patch }));
  }, []);

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (effectiveBrandId) {
        const res = await apiRequest("PATCH", `/api/brands/${effectiveBrandId}`, data);
        return res.json();
      } else {
        const res = await apiRequest("POST", "/api/brands", data);
        return res.json();
      }
    },
    onSuccess: (brand: { id: string }) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands?workspaceId=${workspaceId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", effectiveBrandId || brand.id] });
      toast({ title: effectiveBrandId ? "Brand updated" : "Brand created" });
      navigate(`/brands/${effectiveBrandId || brand.id}`);
    },
    onError: (e: Error) => toast({ title: effectiveBrandId ? "Error updating brand" : "Error creating brand", description: e.message, variant: "destructive" }),
  });

  const canProceed = currentStep === 0 ? form.name.trim().length > 0 : true;

  // Local form fields wiped by a "Start fresh" rerun. Identity basics
  // (name, slug, url, logoUrl, adgenLogoUrl) and the design system are kept.
  const START_FRESH_FORM_CLEAR: Partial<BrandFormData> = {
    category: "", summary: "", positioning: "", worldview: "", enemy: "",
    promise: "", founderStory: "", mission: "", toneSummary: "",
    approvedPhrases: [], bannedPhrases: [], proofHierarchy: [],
    claimGuardrails: [], visualRules: "", colorPalette: [],
    nonNegotiableTruths: [], categoryLanguage: [], pricePositioning: "",
    targetAudienceSignals: [], keyProducts: [], socialProfiles: "",
    researchSources: [], researchNotes: "", researchConfidence: null,
    strategicPriorities: [],
  };

  const [confirmStartFresh, setConfirmStartFresh] = useState(false);

  const aiResearch = useMutation({
    mutationFn: async (override?: { name?: string; url?: string; logoUrl?: string; startFresh?: boolean }) => {
      const requestedName = (override?.name ?? form.name).trim();
      const requestedUrl = (override?.url ?? form.url).trim();
      const requestedLogoUrl = (override?.logoUrl ?? form.logoUrl).trim();
      if (!requestedName) throw new Error("Brand name is required.");
      if (!requestedUrl) throw new Error("Website URL is required.");

      // If the brand hasn't been saved yet, create it on the fly so we have
      // an id to attach the AI research run to.
      let brandId = effectiveBrandId;
      let createdNew = false;
      if (!brandId) {
        const slug = requestedName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        const createPayload: Record<string, unknown> = {
          workspaceId,
          name: requestedName,
          slug,
          url: requestedUrl,
        };
        if (requestedLogoUrl) createPayload.logoUrl = requestedLogoUrl;

        const createRes = await apiRequest("POST", "/api/brands", createPayload);
        const created = (await createRes.json()) as { id: string };
        if (!created?.id) throw new Error("Brand creation failed — no id returned.");
        brandId = created.id;
        createdNew = true;
      }

      const res = await apiRequest("POST", `/api/brands/${brandId}/ai-research`, {
        name: requestedName,
        url: requestedUrl,
        ...(override?.startFresh ? { startFresh: true } : {}),
      });
      const data = (await res.json()) as { status: string; startedAt: string };
      return {
        ...data,
        brandId: brandId!,
        createdNew,
        name: requestedName,
        url: requestedUrl,
        logoUrl: requestedLogoUrl,
        startFresh: override?.startFresh === true,
      };
    },
    onSuccess: (result) => {
      const { brandId, createdNew, name: rName, url: rUrl, logoUrl: rLogo, startFresh } = result;
      if (startFresh) {
        // Reflect the server-side wipe immediately so no old values linger in
        // the form; the merge effect will fill these when the new run lands.
        update({ ...START_FRESH_FORM_CLEAR });
      }
      trackAIResearch(brandId, rName || form.name.trim() || "Brand");
      queryClient.invalidateQueries({ queryKey: [`/api/brands?workspaceId=${workspaceId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId] });

      if (createdNew && !editBrandId) {
        // Intake flow: keep the user on /brands/new and pivot to the
        // researching screen. We track the new brand id locally so polling
        // works without changing the URL.
        setCreatedBrandId(brandId);
        update({
          name: rName,
          url: rUrl,
          logoUrl: rLogo || form.logoUrl,
          slug: rName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        });
        setPhase("researching");
        toast({
          title: "Brand created — AI Research started",
          description: "We'll auto-fill the wizard once research finishes (typically 4–6 minutes).",
        });
      } else {
        toast({
          title: startFresh ? "Starting fresh — AI Research started" : "AI Research started",
          description: startFresh
            ? "Existing brand content was cleared. All fields will be regenerated (typically 4–6 minutes). Name, URL, and logos were kept."
            : "This typically takes 4–6 minutes. You can keep editing or navigate away — we'll notify you when it's done.",
        });
      }
    },
    onError: (err: Error) => {
      if (phase === "intake") {
        setIntakeError(err.message || "Couldn't start AI research");
      }
      toast({ title: "Couldn't start AI research", description: err.message, variant: "destructive" });
    },
  });

  // When background research completes, merge any newly-enriched fields into the wizard's local form,
  // preserving anything the user has typed.
  useEffect(() => {
    if (!existingBrand) return;
    const completedAt = (existingBrand as any)?.aiResearchResult?.completedAt;
    if (aiStatus !== "completed" || !completedAt) return;
    const key = `${effectiveBrandId}:${completedAt}`;
    if (mergedResultIdRef.current === key) return;
    mergedResultIdRef.current = key;

    const e = (existingBrand as any).aiResearchResult?.enhanced as Partial<BrandFormData> | undefined;
    if (!e) return;
    {
      const patch: Partial<BrandFormData> = {};
      const setIfEmpty = <K extends keyof BrandFormData>(k: K, v: BrandFormData[K] | undefined) => {
        if (v === undefined || v === null) return;
        const cur = form[k] as unknown;
        if ((BRAND_TAG_FIELDS as readonly string[]).includes(k)) {
          if (hasBrandValue(v) && !hasBrandValue(cur)) (patch as any)[k] = v;
          return;
        }
        if (k === "colorPalette") {
          if (!form.colorPalette.length) patch.colorPalette = editablePalette(v);
          return;
        }
        if (Array.isArray(v)) {
          if (v.length && (!Array.isArray(cur) || (cur as unknown[]).length === 0)) (patch as any)[k] = v;
        } else if (typeof v === "string") {
          if (v && (!cur || (typeof cur === "string" && !cur.trim()))) (patch as any)[k] = v;
        }
      };
      setIfEmpty("summary", e.summary as any);
      setIfEmpty("category", e.category as any);
      setIfEmpty("positioning", e.positioning as any);
      setIfEmpty("worldview", e.worldview as any);
      setIfEmpty("enemy", e.enemy as any);
      setIfEmpty("promise", e.promise as any);
      setIfEmpty("founderStory", e.founderStory as any);
      setIfEmpty("mission", e.mission as any);
      setIfEmpty("toneSummary", e.toneSummary as any);
      setIfEmpty("visualRules", e.visualRules as any);
      setIfEmpty("approvedPhrases", e.approvedPhrases as any);
      setIfEmpty("bannedPhrases", e.bannedPhrases as any);
      setIfEmpty("proofHierarchy", e.proofHierarchy as any);
      setIfEmpty("claimGuardrails", e.claimGuardrails as any);
      setIfEmpty("nonNegotiableTruths", e.nonNegotiableTruths as any);
      setIfEmpty("colorPalette", e.colorPalette as any);
      setIfEmpty("strategicPriorities", (e as any).strategicPriorities);
      setIfEmpty("categoryLanguage", (e as any).categoryLanguage);
      setIfEmpty("pricePositioning", (e as any).pricePositioning);
      setIfEmpty("targetAudienceSignals", (e as any).targetAudienceSignals);
      setIfEmpty("keyProducts", (e as any).keyProducts);
      setIfEmpty("researchSources", (e as any).researchSources);
      setIfEmpty("researchNotes", (e as any).researchNotes);
      setIfEmpty("productPhilosophy", (e as any).productPhilosophy);
      setIfEmpty("officialUrl", (e as any).officialUrl);
      // socialProfiles: stringify if object
      const sp = (e as any).socialProfiles;
      if (sp && !form.socialProfiles) {
        if (typeof sp === "string" && sp.trim()) (patch as any).socialProfiles = sp;
        else if (typeof sp === "object") {
          try { (patch as any).socialProfiles = JSON.stringify(sp, null, 2); } catch {}
        }
      }
      const rc = (e as any).researchConfidence;
      if (typeof rc === "number" && form.researchConfidence == null) {
        (patch as any).researchConfidence = rc;
      }
      const filledKeys = Object.keys(patch);
      update(patch);
      const partial = (existingBrand as any)?.aiResearchResult?.partial === true;
      toast({
        title: partial ? "AI research only filled the basics" : "Brand enhanced with AI research",
        description: partial
          ? "The deeper sections mostly came back empty — try re-running AI Research to fill them in. Existing values were preserved."
          : filledKeys.length
            ? `Filled ${filledKeys.length} empty field${filledKeys.length === 1 ? "" : "s"}. Existing values were preserved.`
            : "All fields already had values — nothing was overwritten.",
        variant: partial ? "destructive" : undefined,
      });
    }
  }, [existingBrand, aiStatus, editBrandId, form, update, toast]);

  const aiResearchDisabledReason = !form.name.trim()
    ? "Add a brand name first"
    : !form.url.trim()
      ? "Add a website URL first"
      : "";

  const handleSubmit = () => {
    const payload = buildBrandUpdatePayload(form, { isEditMode, workspaceId });
    // The palette editor uses swatches, while SMB may store bare HEX strings.
    // Do not rewrite that independent JSON contract on an unrelated save.
    if (existingBrand && !paletteEdited.current) delete payload.colorPalette;
    saveMutation.mutate(payload);
  };

  const progressPercent = ((currentStep + 1) / STEPS.length) * 100;

  const stepHasContent = (stepIdx: number): boolean => {
    switch (stepIdx) {
      case 0: return !!form.name;
      case 1: return !!(form.positioning || form.worldview || form.enemy || form.promise || form.founderStory || form.mission);
      case 2: return !!(form.toneSummary || hasBrandValue(form.approvedPhrases) || hasBrandValue(form.bannedPhrases));
      case 3: return !!(hasBrandValue(form.proofHierarchy) || hasBrandValue(form.claimGuardrails) || form.visualRules || hasBrandValue(form.nonNegotiableTruths));
      case 4: {
        const ds = form.brandDesignSystem as any;
        return !!(
          ds?.designPositioning ||
          (ds?.visualMood?.length ?? 0) ||
          (ds?.primaryColors?.length ?? 0) ||
          (ds?.secondaryColors?.length ?? 0) ||
          (ds?.accentColors?.length ?? 0) ||
          ds?.typographyDirection?.headlineStyle ||
          ds?.typographyDirection?.bodyStyle ||
          ds?.typographyDirection?.accentStyle ||
          (ds?.fonts?.length ?? 0) ||
          ds?.layoutDirection?.overallStyle ||
          ds?.layoutDirection?.sectionDensity ||
          (ds?.layoutDirection?.preferredLayouts?.length ?? 0) ||
          (ds?.layoutDirection?.avoidLayouts?.length ?? 0)
        );
      }
      case 5: return !!(
        hasBrandValue(form.categoryLanguage) || form.pricePositioning || hasBrandValue(form.targetAudienceSignals) ||
        hasBrandValue(form.keyProducts) || hasBrandValue(form.strategicPriorities) || hasBrandValue(form.researchSources) ||
        form.researchNotes || form.socialProfiles || form.researchConfidence != null
      );
      default: return false;
    }
  };

  // Brand creation is for masters or users granted the per-user
  // "can create brands" capability; the API also enforces this with a 403.
  // Edit mode (/brands/:brandId/settings) is unaffected.
  if (!isEditMode && !authLoading && !user?.isMaster && !user?.canCreateBrands) {
    return <BrandCreateAccessDenied />;
  }

  if (phase === "intake") {
    return (
      <IntakeScreen
        name={intake.name}
        url={intake.url}
        logoUrl={intake.logoUrl}
        logoMode={intake.logoMode}
        onChange={(p) => {
          setIntake((prev) => ({ ...prev, ...p }));
          setIntakeError("");
        }}
        isSubmitting={aiResearch.isPending}
        errorMessage={intakeError}
        onSubmit={() =>
          aiResearch.mutate({
            name: intake.name,
            url: intake.url,
            logoUrl: intake.logoUrl,
          })
        }
      />
    );
  }

  if (phase === "researching") {
    const failed = aiStatus === "failed";
    return (
      <ResearchingScreen
        brandName={form.name || intake.name}
        status={failed ? "failed" : "running"}
        startedAt={aiStartedAt}
        errorMessage={aiResearchError || ""}
        retryDisabled={aiResearch.isPending}
        onRetry={() => aiResearch.mutate(undefined)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-background" data-testid="brand-wizard">
      <div className="shrink-0 border-b bg-card px-6 py-4" data-testid="wizard-progress-header">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex items-center gap-4">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full hover:bg-muted" onClick={() => navigate(effectiveBrandId ? `/brands/${effectiveBrandId}` : "/brands")} data-testid="button-back-to-brands">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight truncate">
                {isEditMode ? `Edit: ${form.name}` : (form.name || "New Brand")}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Step {currentStep + 1} of {STEPS.length} — {STEPS[currentStep].label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!!aiResearchDisabledReason || aiResearch.isPending || isResearching}
                  title={aiResearchDisabledReason || (isResearching ? "AI research is already running for this brand" : "Re-run Grok AI research")}
                  data-testid="button-ai-research"
                >
                  <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${aiResearch.isPending || isResearching ? "animate-pulse" : ""}`} />
                  {aiResearch.isPending ? "Starting…" : isResearching ? "Researching…" : "Re-run AI Research"}
                  <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => aiResearch.mutate(undefined)}
                  data-testid="menu-rerun-fill-blanks"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-2" />
                  <div className="flex flex-col">
                    <span>Fill in blanks only</span>
                    <span className="text-[11px] text-muted-foreground">Only empty fields get filled — existing content is kept</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setConfirmStartFresh(true)}
                  className="text-destructive focus:text-destructive"
                  data-testid="menu-rerun-start-fresh"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  <div className="flex flex-col">
                    <span>Start fresh</span>
                    <span className="text-[11px] text-muted-foreground">Erase all researched content and regenerate everything</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog open={confirmStartFresh} onOpenChange={setConfirmStartFresh}>
              <AlertDialogContent data-testid="dialog-confirm-start-fresh">
                <AlertDialogHeader>
                  <AlertDialogTitle>Start fresh and regenerate everything?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This erases all existing brand doctrine content — positioning, tone &amp; voice,
                    guardrails, market info, social profiles, and every other AI-researched field —
                    and regenerates it from scratch. Your brand name, website URL, and logos are kept.
                    This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-cancel-start-fresh">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => aiResearch.mutate({ startFresh: true })}
                    data-testid="button-confirm-start-fresh"
                  >
                    Erase &amp; regenerate
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <div className="hidden sm:flex items-center gap-1.5">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i <= currentStep ? "w-8 bg-primary" : "w-4 bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="sm:hidden mt-3 h-1 w-full rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            data-testid="progress-bar-wizard"
          />
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="w-60 shrink-0 border-r bg-muted/20 hidden md:block" data-testid="wizard-step-sidebar">
          <ScrollArea className="h-full">
            <div className="p-5 space-y-1">
              <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">
                Steps
              </h3>
              {STEPS.map((step, index) => {
                const isActive = index === currentStep;
                const completed = index < STEPS.length - 1 && stepHasContent(index);
                const Icon = step.icon;
                const isClickable = index === 0 || form.name.trim().length > 0;

                return (
                  <button
                    key={step.id}
                    onClick={() => isClickable && setCurrentStep(index)}
                    disabled={!isClickable}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : isClickable ? "hover:bg-muted/60" : "opacity-40 cursor-not-allowed"
                    }`}
                    data-testid={`button-step-${step.id}`}
                  >
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                      isActive
                        ? "bg-primary-foreground/20"
                        : completed ? "bg-primary/10" : "bg-muted"
                    }`}>
                      {completed && !isActive ? (
                        <Check className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <Icon className={`h-3.5 w-3.5 ${isActive ? "text-primary-foreground" : "text-muted-foreground"}`} />
                      )}
                    </div>
                    <span className={`text-xs font-medium ${
                      isActive ? "text-primary-foreground" : "text-muted-foreground"
                    }`}>
                      {step.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <div className="flex-1 min-w-0 flex flex-col" data-testid="wizard-main-panel">
          <ScrollArea className="flex-1">
            <div className="p-8 max-w-2xl mx-auto">
              {(isResearching || aiStatus === "failed") && (
                <AIResearchProgress
                  status={aiStatus as "running" | "failed"}
                  startedAt={aiStartedAt}
                  errorMessage={aiResearchError || ""}
                />
              )}
              {aiResearchPartial && !isResearching && (
                <Card className="mb-6 border-amber-500/40 bg-amber-500/5" data-testid="ai-research-partial-card">
                  <CardContent className="p-5 flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-amber-700 dark:text-amber-500">
                        AI research only filled the basics
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 break-words">
                        The deeper sections (Strategic DNA, Tone &amp; Voice, Guardrails, Market &amp; Audience) mostly came back empty. This can happen when the research run couldn't find much public information about the brand, or the website couldn't be read properly. Re-running usually fills them in.
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-3 border-amber-500/40"
                        onClick={() => aiResearch.mutate(undefined)}
                        disabled={!!aiResearchDisabledReason || aiResearch.isPending || isResearching}
                        data-testid="button-rerun-partial-research"
                      >
                        <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${aiResearch.isPending ? "animate-pulse" : ""}`} />
                        {aiResearch.isPending ? "Starting…" : "Re-run AI Research"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {currentStep === 0 && <StepBasics form={form} update={update} editBrandId={isEditMode ? editBrandId : undefined} />}
                {currentStep === 1 && <StepStrategic form={form} update={update} />}
                {currentStep === 2 && <StepTone form={form} update={update} />}
                {currentStep === 3 && <StepGuardrails form={form} update={update} />}
                {currentStep === 4 && <StepDesignSystem form={form} update={update} brandId={effectiveBrandId} />}
                {currentStep === 5 && <StepAdvanced form={form} update={update} />}
                {currentStep === 6 && <StepReview form={form} onGoToStep={setCurrentStep} />}
              </motion.div>
            </div>
          </ScrollArea>

          <div className="shrink-0 border-t bg-card px-8 py-4 flex items-center justify-between gap-4" data-testid="wizard-navigation">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(s => s - 1)}
              disabled={currentStep === 0}
              className="gap-1.5"
              data-testid="button-go-back"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            {currentStep < STEPS.length - 1 ? (
              <div className="flex items-center gap-2">
                {isEditMode && (
                  <Button
                    variant="outline"
                    onClick={handleSubmit}
                    disabled={!form.name.trim() || saveMutation.isPending}
                    className="gap-1.5 px-5"
                    data-testid="button-save-brand"
                  >
                    {saveMutation.isPending ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                )}
                <Button
                  onClick={() => setCurrentStep(s => s + 1)}
                  disabled={!canProceed}
                  className="gap-1.5 px-6"
                  data-testid="button-go-forward"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!form.name.trim() || saveMutation.isPending}
                className="gap-1.5 px-6"
                data-testid={isEditMode ? "button-save-brand" : "button-create-brand"}
              >
                {saveMutation.isPending ? (
                  <>{isEditMode ? "Saving..." : "Creating..."}</>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {isEditMode ? "Save Changes" : "Create Brand"}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
